// src/features/notifications/context/NotificationProvider.tsx

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { notificationKeys } from '../hooks/useNotifications';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';
import type { NotificationPreferences } from '@/features/edgemail-client/store/settingsStore';
import { connectApi, type ConnectThread } from '@/features/connect/api/connectApi';

type RealtimeNotificationPayload = {
  id?: string;
  title: string;
  message?: string;
  link?: string;
  type?: string;
  metadata?: Record<string, any>;
};

type NotificationToast = {
  localId: string;
  title: string;
  message?: string;
  link?: string;
  type?: string;
};

const TOAST_MAX_STACK = 3;
const TOAST_AUTO_DISMISS_MS = 7000;

type MessageNavigationAttention = {
  /** Unread in un-muted conversations (the quiet dot). */
  unreadCount: number;
  /** Mentions anywhere plus unread direct messages (the red number). A mute never hides these. */
  attentionCount: number;
  mentionCount: number;
  attentionVersion: number;
};

const MessageNavigationAttentionContext = createContext<MessageNavigationAttention>({
  unreadCount: 0,
  attentionCount: 0,
  mentionCount: 0,
  attentionVersion: 0,
});

export const totalUnreadConnectMessages = (threads: ConnectThread[]): number =>
  threads.reduce((total, thread) => total + Math.max(0, Number(thread.unread_count) || 0), 0);

export const shouldTriggerMessageAttention = (previous: number | null, next: number): boolean =>
  previous !== null && next > previous;

export const useMessageNavigationAttention = (): MessageNavigationAttention =>
  useContext(MessageNavigationAttentionContext);

export function shouldDisplayRealtimeNotification(
  data: RealtimeNotificationPayload,
  preferences: NotificationPreferences,
): boolean {
  if (!preferences.notificationsEnabled) return false;

  const sourceApp = typeof data.metadata?.source_app === 'string' ? data.metadata.source_app : undefined;
  if (sourceApp === 'calendar' || data.type === 'calendar_reminder') {
    return preferences.calendarNotifications && preferences.calendarEventReminders;
  }

  if (sourceApp === 'documents' || sourceApp === 'dms' || data.type?.startsWith('document_')) {
    return preferences.documentsNotifications;
  }

  return true;
}

/**
 * Connects to the /notifications WebSocket namespace and invalidates
 * React Query caches on new notification events. Also triggers a
 * browser desktop notification if permission is granted.
 */
export default function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { keycloak } = useKeycloak();
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const toastTimersRef = useRef<Map<string, number>>(new Map());
  const [toasts, setToasts] = useState<NotificationToast[]>([]);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [messageAttentionCount, setMessageAttentionCount] = useState(0);
  const [messageMentionCount, setMessageMentionCount] = useState(0);
  const [messageAttentionVersion, setMessageAttentionVersion] = useState(0);
  const messageUnreadCountRef = useRef<number | null>(null);
  const messageUnreadRefreshRequestRef = useRef(0);
  const notificationPreferences = useUserPreferencesStore((state) => state.notificationPreferences);

  // R2 (2026-09-08): one aggregate query, shared with the mobile badge, instead of
  // fetching every conversation the user belongs to just to add up a number.
  const refreshMessageUnreadCount = useCallback(async (animateOnIncrease = false) => {
    const requestNumber = ++messageUnreadRefreshRequestRef.current;
    try {
      const counts = await connectApi.getUnreadCount();
      if (requestNumber !== messageUnreadRefreshRequestRef.current) return;
      const nextCount = Math.max(0, Number(counts.unread_count) || 0);
      const nextAttention = Math.max(0, Number(counts.mention_count) || 0) + Math.max(0, Number(counts.direct_unread_count) || 0);
      if (animateOnIncrease && shouldTriggerMessageAttention(messageUnreadCountRef.current, nextCount + nextAttention)) {
        setMessageAttentionVersion((current) => current + 1);
      }
      messageUnreadCountRef.current = nextCount + nextAttention;
      setMessageUnreadCount(nextCount);
      setMessageAttentionCount(nextAttention);
      setMessageMentionCount(Math.max(0, Number(counts.mention_count) || 0));
    } catch {
      // Navigation attention is additive. A transient count failure must not
      // disrupt Messages or the notification socket itself.
    }
  }, []);

  useEffect(() => {
    void refreshMessageUnreadCount();
    const refreshReadState = () => { void refreshMessageUnreadCount(); };
    window.addEventListener('edgelex-connect-read-state-changed', refreshReadState);
    return () => window.removeEventListener('edgelex-connect-read-state-changed', refreshReadState);
  }, [refreshMessageUnreadCount]);

  const dismissToast = useCallback((toastId: string) => {
    const existingTimer = toastTimersRef.current.get(toastId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      toastTimersRef.current.delete(toastId);
    }
    setToasts((current) => current.filter((toast) => toast.localId !== toastId));
  }, []);

  const addToast = useCallback((data: RealtimeNotificationPayload) => {
    const localId = data.id
      ? `notification:${data.id}`
      : `ephemeral:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

    setToasts((current) => {
      if (data.id && current.some((toast) => toast.localId === localId)) {
        return current;
      }

      return [
        {
          localId,
          title: data.title,
          message: data.message,
          link: data.link,
          type: data.type,
        },
        ...current,
      ].slice(0, TOAST_MAX_STACK);
    });

    const existingTimer = toastTimersRef.current.get(localId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }
    const timeoutId = window.setTimeout(() => dismissToast(localId), TOAST_AUTO_DISMISS_MS);
    toastTimersRef.current.set(localId, timeoutId);
  }, [dismissToast]);

  const handleToastOpen = useCallback((toast: NotificationToast) => {
    if (toast.link) {
      window.location.href = toast.link;
    }
    dismissToast(toast.localId);
  }, [dismissToast]);

  const toastAccentClass = useCallback((type?: string) => {
    switch (type) {
      case 'task_overdue':
      case 'matter_deleted':
        return 'bg-red-100 text-red-700';
      case 'task_completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'task_assigned':
      case 'matter_created':
      case 'matter_document_uploaded':
        return 'bg-blue-100 text-blue-700';
      case 'matter_status_changed':
      case 'matter_transferred':
        return 'bg-purple-100 text-purple-700';
      case 'matter_archived':
        return 'bg-gray-100 text-gray-700';
      case 'task_reminder':
      default:
        return 'bg-amber-100 text-amber-700';
    }
  }, []);

  useEffect(() => {
    const token = keycloak.token || localStorage.getItem('edgelex_access_token');
    if (!token) return;

    const socket = io('/notifications', {
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('notification:new', (data: RealtimeNotificationPayload) => {
      // Invalidate queries so the bell badge + dropdown refresh
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      const shouldDisplay = shouldDisplayRealtimeNotification(data, notificationPreferences);
      if (shouldDisplay) {
        addToast(data);
      }

      // Desktop notification — for agent triggers, respect the per-trigger preference;
      // for all other notification types, always show if permission is granted.
      const showDesktop = data.metadata?.showDesktopNotification !== false;
      if (shouldDisplay && showDesktop && 'Notification' in window && window.Notification.permission === 'granted') {
        const n = new window.Notification(data.title, {
          body: data.message,
          icon: '/favicon.ico',
          tag: 'edgelex-notification',
        });
        if (data.link) {
          n.onclick = () => {
            window.focus();
            window.location.href = data.link!;
          };
        }
      }
    });

    // Connect intentionally transmits only routing metadata. The authorized
    // Messages surface refetches durable content rather than receiving it over
    // Socket.IO, which keeps document/context text off the realtime channel.
    socket.on('connect:message', (data: { thread_id: string; message_id: string | null; sequence: number; sender_user_id?: string | null }) => {
      window.dispatchEvent(new CustomEvent('edgelex-connect-message', { detail: data }));
      if (data.message_id) void refreshMessageUnreadCount(true);
    });

    // Ephemeral typing signal (Wave 6 polish) — routing metadata only,
    // rebroadcast as a window event for the Messages surface.
    socket.on('connect:typing', (data: { thread_id: string; user_id: string; display_name: string }) => {
      window.dispatchEvent(new CustomEvent('edgelex-connect-typing', { detail: data }));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      toastTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      toastTimersRef.current.clear();
    };
  }, [addToast, keycloak.token, notificationPreferences, qc, refreshMessageUnreadCount]);

  // Request desktop notification permission once
  useEffect(() => {
    if ('Notification' in window && window.Notification.permission === 'default') {
      window.Notification.requestPermission();
    }
  }, []);

  const messageNavigationAttention = useMemo(() => ({
    unreadCount: messageUnreadCount,
    attentionCount: messageAttentionCount,
    mentionCount: messageMentionCount,
    attentionVersion: messageAttentionVersion,
  }), [messageUnreadCount, messageAttentionCount, messageMentionCount, messageAttentionVersion]);

  return (
    <MessageNavigationAttentionContext.Provider value={messageNavigationAttention}>
      {children}
      <div className="pointer-events-none fixed right-4 top-20 z-[9999] flex w-[min(92vw,24rem)] flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.localId}
            className="pointer-events-auto rounded-xl border border-[var(--lex-border)] bg-[var(--lex-surface-card)] shadow-xl"
          >
            <div className="flex items-start gap-3 p-3">
              <div className={`mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${toastAccentClass(toast.type)}`}>
                <BellIcon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[var(--lex-text-primary)]">{toast.title}</p>
                {toast.message && (
                  <p className="mt-1 text-xs text-[var(--lex-text-secondary)]">{toast.message}</p>
                )}
                {toast.link && (
                  <button
                    onClick={() => handleToastOpen(toast)}
                    className="mt-2 text-xs font-medium text-[var(--lex-accent-primary)] hover:text-[var(--lex-accent-primary-hover)]"
                  >
                    Open
                  </button>
                )}
              </div>
              <button
                onClick={() => dismissToast(toast.localId)}
                className="rounded p-1 text-[var(--lex-text-tertiary)] hover:bg-[var(--lex-surface-hover)] hover:text-[var(--lex-text-primary)]"
                aria-label="Dismiss notification"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </MessageNavigationAttentionContext.Provider>
  );
}
